/**
 * FlowAiTokensPanel — admin UI for issuing / listing / revoking
 * scoped read-only bearer tokens that let external AI assistants
 * consume `/api/flow-ai/*`.
 *
 * Access: admin, super_admin, customer_service (enforced by
 * `/api/flow-ai/tokens/*` backend guard `get_flow_admin_user`).
 *
 * Raw tokens are only ever returned once at creation — this UI
 * SURFACES the raw string ONE TIME in a copy-to-clipboard dialog
 * and never persists it client-side.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from './ui/dialog';
import { Badge } from './ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Plus, Copy, Trash2, KeyRound, Clock, ShieldCheck, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(ts) {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function relative(ts) {
    if (!ts) return 'never';
    const then = new Date(ts).getTime();
    const now = Date.now();
    const s = Math.round((then - now) / 1000);
    if (s < -86400) return `${Math.round(-s / 86400)}d ago`;
    if (s < -3600)  return `${Math.round(-s / 3600)}h ago`;
    if (s < -60)    return `${Math.round(-s / 60)}m ago`;
    if (s <= 0)     return `${-s}s ago`;
    if (s > 86400)  return `in ${Math.round(s / 86400)}d`;
    return `in ${Math.round(s / 3600)}h`;
}

export default function FlowAiTokensPanel() {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [newLabel, setNewLabel] = useState('');
    const [newTtl, setNewTtl] = useState(30);
    const [newRl, setNewRl] = useState(60);
    const [creating, setCreating] = useState(false);
    const [rawTokenReveal, setRawTokenReveal] = useState(null); // { token, record }

    const authHeaders = () => {
        const t = localStorage.getItem('token');
        return t ? { Authorization: `Bearer ${t}` } : {};
    };

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/flow-ai/tokens`, { headers: authHeaders() });
            setTokens(res.data?.tokens || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load tokens');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const createToken = async () => {
        if (!newLabel.trim()) return toast.error('Label is required');
        setCreating(true);
        try {
            const res = await axios.post(`${API}/api/flow-ai/tokens`, {
                label: newLabel.trim(),
                ttl_days: Number(newTtl),
                rate_limit_per_min: Number(newRl),
            }, { headers: authHeaders() });
            setCreateOpen(false);
            setNewLabel('');
            setRawTokenReveal(res.data);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create token');
        } finally {
            setCreating(false);
        }
    };

    const revokeToken = async (id, label) => {
        if (!window.confirm(`Revoke token "${label}"? External assistants using it will immediately lose access.`)) return;
        try {
            await axios.delete(`${API}/api/flow-ai/tokens/${id}`, { headers: authHeaders() });
            toast.success('Token revoked');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to revoke');
        }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard');
        } catch {
            toast.error('Copy failed — select and copy manually');
        }
    };

    const isRevoked = (t) => !!t.revoked_at;
    const isExpired = (t) => !isRevoked(t) && t.expires_at && new Date(t.expires_at) < new Date();

    return (
        <div className="space-y-6" data-testid="flow-ai-tokens-panel">
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <KeyRound className="h-5 w-5" /> Flow AI Access Tokens
                        </CardTitle>
                        <CardDescription>
                            Scoped read-only bearer tokens for external AI assistants. Tokens grant access to
                            <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[11px]">/api/flow-ai/*</code>
                            (orders, notes, activity, files, customer info). No write endpoints exist for these tokens.
                            Max lifetime 90 days. Rate limit default 60 req/min. Every request is logged to System Logs.
                        </CardDescription>
                    </div>
                    <Button onClick={() => setCreateOpen(true)} data-testid="create-flowai-token-btn">
                        <Plus className="h-4 w-4 mr-1.5" /> Create token
                    </Button>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
                    ) : tokens.length === 0 ? (
                        <div className="py-10 text-center text-sm text-muted-foreground">
                            No tokens yet. Create one to give an external AI assistant read-only access.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Label</TableHead>
                                        <TableHead>Prefix</TableHead>
                                        <TableHead>Created by</TableHead>
                                        <TableHead>Expires</TableHead>
                                        <TableHead>Last used</TableHead>
                                        <TableHead>Rate</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {tokens.map(t => (
                                        <TableRow key={t.id} data-testid={`flowai-token-${t.id}`}>
                                            <TableCell className="font-medium">
                                                {t.label}
                                                {t.customer_service_issued && (
                                                    <Badge variant="outline" className="ml-2 text-[10px]">CS</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <code className="text-xs text-muted-foreground">{t.token_prefix}…</code>
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {t.created_by?.name || '—'}
                                                <div className="text-[10px] text-muted-foreground">{fmt(t.created_at)}</div>
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                <div className="flex items-center gap-1">
                                                    <Clock className="h-3 w-3 text-muted-foreground" />
                                                    <span>{relative(t.expires_at)}</span>
                                                </div>
                                                <div className="text-[10px] text-muted-foreground">{fmt(t.expires_at)}</div>
                                            </TableCell>
                                            <TableCell className="text-xs">{relative(t.last_used_at)}</TableCell>
                                            <TableCell className="text-xs">{t.rate_limit_per_min}/min</TableCell>
                                            <TableCell>
                                                {isRevoked(t) ? (
                                                    <Badge variant="destructive">Revoked</Badge>
                                                ) : isExpired(t) ? (
                                                    <Badge variant="outline" className="text-orange-600 border-orange-300">Expired</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                                                        <ShieldCheck className="h-3 w-3 mr-1" /> Active
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {!isRevoked(t) && (
                                                    <Button
                                                        size="sm" variant="ghost"
                                                        onClick={() => revokeToken(t.id, t.label)}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        data-testid={`revoke-flowai-token-${t.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create Flow AI Token</DialogTitle>
                        <DialogDescription>
                            Read-only access to <code>/api/flow-ai/*</code>. The raw token is shown once — copy it immediately.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="fa-label">Label</Label>
                            <Input
                                id="fa-label"
                                placeholder="e.g. Claude — Order Insights"
                                value={newLabel}
                                onChange={e => setNewLabel(e.target.value)}
                                data-testid="new-flowai-label"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="fa-ttl">Expires in (days, max 90)</Label>
                                <Input
                                    id="fa-ttl" type="number" min={1} max={90}
                                    value={newTtl} onChange={e => setNewTtl(e.target.value)}
                                    data-testid="new-flowai-ttl"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="fa-rl">Rate limit (req/min)</Label>
                                <Input
                                    id="fa-rl" type="number" min={1} max={600}
                                    value={newRl} onChange={e => setNewRl(e.target.value)}
                                    data-testid="new-flowai-rl"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button onClick={createToken} disabled={creating} data-testid="submit-flowai-token">
                            {creating ? 'Creating…' : 'Create token'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* One-time reveal dialog */}
            <Dialog open={!!rawTokenReveal} onOpenChange={() => setRawTokenReveal(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-orange-500" /> Copy this token now
                        </DialogTitle>
                        <DialogDescription>
                            This is the only time the raw token is shown. Store it in your AI assistant's secret manager. If lost, revoke and issue a new one.
                        </DialogDescription>
                    </DialogHeader>
                    {rawTokenReveal && (
                        <div className="space-y-3">
                            <div className="rounded-md border bg-muted/30 p-3 font-mono text-sm break-all" data-testid="raw-token-value">
                                {rawTokenReveal.token}
                            </div>
                            <Button onClick={() => copy(rawTokenReveal.token)} className="w-full" data-testid="copy-raw-token">
                                <Copy className="h-4 w-4 mr-1.5" /> Copy to clipboard
                            </Button>
                            <div className="text-xs text-muted-foreground">
                                Label: <strong>{rawTokenReveal.record?.label}</strong> — Expires: {fmt(rawTokenReveal.record?.expires_at)} — Rate: {rawTokenReveal.record?.rate_limit_per_min}/min
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setRawTokenReveal(null)}>Done — I've copied it</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
